import { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { SocketContext } from "../../context/socket";
import useStandingsHandler from "../../hooks/useStandingsHandler";
import StandingsSelect from "../headtohead/StandingsSelect";

export default function AllPlayersGrid({
   loadDownloader,
   clearDownloader
}) {
   const socket = useContext(SocketContext);
   const [selectedRound, setSelectedRound] = useState(0);
   const [selectedArchetype, setSelectedArchetype] = useState(0); // 0=None, 1=Archetype, 2=Team, 3=Strixhaven School
   const [selectedRows, setSelectedRows] = useState(8);
   const [selectedColumns, setSelectedColumns] = useState(1);
   const [allPlayersData, setAllPlayersData] = useState([]);

   const [
      standings,
      setStandings,
      cachedStandings,
      tournamentRounds,
      notificationStandings,
      {
         getCachedStandings,
         getTournamentRounds,
      },
   ] = useStandingsHandler();

   useEffect(() => {
      getCachedStandings();
   }, []);

   useEffect(() => {
      // Load players data when round changes
      if (selectedRound !== null && selectedRound !== undefined) {
         const roundId = selectedRound === 0 || selectedRound === '0' ? 0 : (selectedRound === 9999999 || selectedRound === '9999999' ? 9999999 : Number(selectedRound));
         
         if (roundId === 9999999) {
            // Round 0 (Alphabetical) - get all players
            socket.emit("getPlayers", (allPlayers) => {
               if (allPlayers && allPlayers.length > 0) {
                  // Sort players alphabetically
                  const sortedPlayers = [...allPlayers].sort((a, b) => {
                     const nameA = (a.PlayerNameLastFirst || `${a.LastName || ''} ${a.FirstName || ''}`).toUpperCase();
                     const nameB = (b.PlayerNameLastFirst || `${b.LastName || ''} ${b.FirstName || ''}`).toUpperCase();
                     return nameA.localeCompare(nameB);
                  });
                  
                  // Transform to grid format
                  const gridData = sortedPlayers.map((player, index) => ({
                     id: player.id || player.PlayerId,
                     Rank: index + 1,
                     PlayerName: player.PlayerName || player.PlayerNameLastFirst || `${player.FirstName || ''} ${player.LastName || ''}`.trim(),
                     MatchWins: 0,
                     MatchLosses: 0,
                     MatchDraws: 0,
                     Archetype: player.Archetype || "",
                     TEAM: player.TEAM || "",
                  }));
                  
                  setAllPlayersData(gridData);
               } else {
                  setAllPlayersData([]);
               }
            });
         } else {
            // Regular round - get players data
            socket.emit("getCustomLeaderboardAllPlayers", roundId, (playersData) => {
               if (playersData && playersData.length > 0) {
                  // Transform to grid format
                  const gridData = playersData.map((player, index) => ({
                     id: player.id || player.PlayerId || index,
                     Rank: player.Rank || player.rank || index + 1,
                     PlayerName: player.PlayerName || `${player.FirstName || ''} ${player.LastName || ''}`.trim(),
                     MatchWins: player.MatchWins || 0,
                     MatchLosses: player.MatchLosses || 0,
                     MatchDraws: player.MatchDraws || 0,
                     Archetype: player.FormatDecklistName || player.Archetype || "",
                     TEAM: player.TEAM || "",
                  }));
                  
                  setAllPlayersData(gridData);
               } else {
                  setAllPlayersData([]);
               }
            });
         }
      }
   }, [selectedRound]);

   const handleStandingsChange = (data) => {
      if (data && data.id !== null && data.id !== undefined) {
         // Round 0 (Alphabetical) is value 9999999
         const roundId = data.id === '0' ? 0 : (data.id === '9999999' ? 9999999 : Number(data.id));
         setSelectedRound(roundId);
      }
   };

   const handleArchetypeChange = (e) => {
      setSelectedArchetype(e.target.value);
   };

   const handleRowsChange = (e) => {
      const value = parseInt(e.target.value) || 8;
      setSelectedRows(Math.max(1, Math.min(32, value)));
   };

   const handleColumnsChange = (e) => {
      const value = parseInt(e.target.value) || 1;
      const newColumns = Math.max(1, Math.min(4, value));
      setSelectedColumns(newColumns);
      // If columns is 2, automatically set archetype to 0 (None)
      if (newColumns === 2) {
         setSelectedArchetype(0);
      }
   };

   const handleLoad = () => {
      const roundId = selectedRound === 0 || selectedRound === '0' ? 0 : Number(selectedRound);
      
      // Check if Round 0 (Alphabetical) is selected
      if (selectedRound === 9999999 || selectedRound === '9999999') {
         // Get all players and sort alphabetically
         socket.emit("getPlayers", (allPlayers) => {
            if (!allPlayers || allPlayers.length === 0) {
               alert("No players found.");
               return;
            }

            // Sort players alphabetically by name (Last, First)
            const sortedPlayers = [...allPlayers].sort((a, b) => {
               const nameA = (a.PlayerNameLastFirst || `${a.LastName || ''} ${a.FirstName || ''}`).toUpperCase();
               const nameB = (b.PlayerNameLastFirst || `${b.LastName || ''} ${b.FirstName || ''}`).toUpperCase();
               return nameA.localeCompare(nameB);
            });

            // Extract player IDs and create data structure with rank and 0 0 0 for W L D
            const playerIds = sortedPlayers.map((player) => player.id || player.PlayerId).filter(Boolean);

            if (playerIds.length === 0) {
               alert("No player IDs found.");
               return;
            }

            // Create params object with all settings
            // If columns is 2, force archetype to 0 (None) to hide archetype/team column
            const params = {
               roundId: 9999999, // Round 0 (Alphabetical)
               playerIds: playerIds,
               archetype: selectedColumns === 2 ? 0 : selectedArchetype,
               columns: selectedColumns,
               rows: selectedRows,
               isAlphabetical: true // Flag to indicate alphabetical sorting
            };

            loadDownloader("allplayers", 9999999, JSON.stringify(params), "Standings_Round0_Alphabetical");
         });
      } else {
         // Get all players data for the selected round
         socket.emit("getCustomLeaderboardAllPlayers", roundId, (allPlayersData) => {
            if (!allPlayersData || allPlayersData.length === 0) {
               alert("No players data available for the selected round.");
               return;
            }

            // Extract player IDs from the standings data
            const playerIds = allPlayersData.map((item) => {
               return item.id || item.PlayerId;
            }).filter(Boolean);

            if (playerIds.length === 0) {
               alert("No player IDs found in standings data.");
               return;
            }

            // Create params object with all settings
            // If columns is 2, force archetype to 0 (None) to hide archetype/team column
            const params = {
               roundId: roundId,
               playerIds: playerIds,
               archetype: selectedColumns === 2 ? 0 : selectedArchetype,
               columns: selectedColumns,
               rows: selectedRows
            };

            loadDownloader("allplayers", roundId, JSON.stringify(params), "Standings_Round" + roundId);
         });
      }
   };

   const refreshGrid = () => {
      getCachedStandings();
      // Reload players data
      const roundId = selectedRound === 0 || selectedRound === '0' ? 0 : (selectedRound === 9999999 || selectedRound === '9999999' ? 9999999 : Number(selectedRound));
      if (roundId === 9999999) {
         socket.emit("getPlayers", (allPlayers) => {
            if (allPlayers && allPlayers.length > 0) {
               const sortedPlayers = [...allPlayers].sort((a, b) => {
                  const nameA = (a.PlayerNameLastFirst || `${a.LastName || ''} ${a.FirstName || ''}`).toUpperCase();
                  const nameB = (b.PlayerNameLastFirst || `${b.LastName || ''} ${b.FirstName || ''}`).toUpperCase();
                  return nameA.localeCompare(nameB);
               });
               const gridData = sortedPlayers.map((player, index) => ({
                  id: player.id || player.PlayerId,
                  Rank: index + 1,
                  PlayerName: player.PlayerName || player.PlayerNameLastFirst || `${player.FirstName || ''} ${player.LastName || ''}`.trim(),
                  MatchWins: 0,
                  MatchLosses: 0,
                  MatchDraws: 0,
                  Archetype: player.Archetype || "",
                  TEAM: player.TEAM || "",
               }));
               setAllPlayersData(gridData);
            }
         });
      } else {
         socket.emit("getCustomLeaderboardAllPlayers", roundId, (playersData) => {
            if (playersData && playersData.length > 0) {
               const gridData = playersData.map((player, index) => ({
                  id: player.id || player.PlayerId || index,
                  Rank: player.Rank || player.rank || index + 1,
                  PlayerName: player.PlayerName || `${player.FirstName || ''} ${player.LastName || ''}`.trim(),
                  MatchWins: player.MatchWins || 0,
                  MatchLosses: player.MatchLosses || 0,
                  MatchDraws: player.MatchDraws || 0,
                  Archetype: player.FormatDecklistName || player.Archetype || "",
                  TEAM: player.TEAM || "",
               }));
               setAllPlayersData(gridData);
            }
         });
      }
      clearDownloader();
   };

   let columns;
   let initialState;

   columns = [
      {
         field: "Rank",
         headerName: "Rank",
         editable: false,
         width: 100,
         filterable: false,
      },
      {
         field: "PlayerName",
         headerName: "Player Name",
         editable: false,
         width: 300,
      },
      {
         field: "MatchWins",
         headerName: "W",
         editable: false,
         width: 80,
      },
      {
         field: "MatchLosses",
         headerName: "L",
         editable: false,
         width: 80,
      },
      {
         field: "MatchDraws",
         headerName: "D",
         editable: false,
         width: 80,
      },
      {
         field: "Archetype",
         headerName: "Archetype",
         editable: false,
         width: 250,
      },
      {
         field: "TEAM",
         headerName: "Team",
         editable: false,
         width: 200,
      },
   ];

   initialState = {
      sorting: {
         sortModel: [{ field: "Rank", sort: "asc" }],
      },
   };

   return (
      <>
         <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h3" component="div" sx={{ textTransform: "uppercase" }}>Player Standings</Typography>
            <Button onClick={refreshGrid} color="error" variant="outlined">Refresh Data</Button>
         </Stack>
         
         <Box sx={{ py: 2, px: 4, backgroundColor: "#fff", mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
               <StandingsSelect 
                  selected={selectedRound}
                  callback={handleStandingsChange}
                  backgroundColor={null}
               />

               {selectedColumns !== 2 && (
                  <FormControl sx={{ minWidth: 220 }}>
                     <InputLabel id="archetype-select-label">SHOW</InputLabel>
                     <Select
                        labelId="archetype-select-label"
                        value={selectedArchetype}
                        label="SHOW"
                        onChange={handleArchetypeChange}
                     >
                        <MenuItem value={0}>None</MenuItem>
                        <MenuItem value={1}>Archetype</MenuItem>
                        <MenuItem value={2}>Team</MenuItem>
                        <MenuItem value={3}>Strixhaven School</MenuItem>
                     </Select>
                  </FormControl>
               )}

               <TextField
                  label="Rows"
                  type="number"
                  value={selectedRows}
                  onChange={handleRowsChange}
                  inputProps={{ min: 1, max: 32 }}
                  sx={{ width: 120 }}
               />

               <TextField
                  label="Columns"
                  type="number"
                  value={selectedColumns}
                  onChange={handleColumnsChange}
                  inputProps={{ min: 1, max: 4 }}
                  sx={{ width: 120 }}
               />

               <Button
                  variant="contained"
                  color="primary"
                  onClick={handleLoad}
               >
                  LOAD
               </Button>
            </Stack>
         </Box>

         <Box height={400} width="100%">
            {allPlayersData && allPlayersData.length > 0 && (
               <DataGrid
                  initialState={initialState}
                  sx={{ backgroundColor: "#efefef" }}
                  rows={allPlayersData}
                  rowHeight={40}
                  columns={columns}
                  rowsPerPageOptions={[100, 200, 300, 1000, 5000]}
                  components={{ Toolbar: GridToolbar }}
                  componentsProps={{
                     toolbar: {
                        printOptions: {
                           disableToolbarButton: true,
                        },
                     },
                  }}
                  disableColumnSelector
                  disableDensitySelector
                  disableSelectionOnClick
               />
            )}
         </Box>
      </>
   );
}
